import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, OnDestroy, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppStartupService, BaseComponent, CardGroup, RelatedCard } from '@ridango/core';
import { RelatedCardFacade } from '@ridango/components';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';

@Component({
    selector: 'rwidget-add-edit-card-form',
    templateUrl: './add-edit-card-form.component.html',
    styleUrls: ['./add-edit-card-form.component.scss']
})
export class AddEditCardFormComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() mode: 'add' | 'edit';
    @Input() editCard: RelatedCard;
    @Output() formChange: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

    showDiscounts: boolean = this.env.RELATED_CARD?.SHOW_DISCOUNTS;
    hidePinField: boolean = this.env.RELATED_CARD?.HIDE_PIN;

    discountGroupOptions: CardGroup[];

    cardForm: FormGroup;

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        @Inject('ENV') public env: Env,
        private relatedCardFacade: RelatedCardFacade,
        private translate: TranslateService,
        private appStartupService: AppStartupService
    ) {
        super();
    }

    ngOnInit(): void {
        this.locale = this.translate.currentLang;
        this.translate.onLangChange
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(() => (this.locale = this.translate.currentLang));
        this.fetchCardDiscountOptions();
        if (this.mode === 'add') {
            this.createFormGroup();
        }
        if (this.mode === 'edit') {
            this.relatedCardFacade.selectedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card) => {
                if (card) {
                    this.createFormGroup(card);
                }
            });
        }
        this.relatedCardFacade.allDiscountGroups$.pipe(takeUntil(this.componentDestroyed$)).subscribe((groups) => {
            const webDiscountGroups = this.appStartupService.getPropertyByCode('WEB_DISCOUNT_GROUPS')?.value;
            /*
             * WEB_DISCOUNT_GROUPS could be one of the following:
             * - a string of ids separated by commas (no, not an array of numbers, that would make too much sense).
             * - undefined (maybe it's some kind of a statement, who knows).
             * - 'null' (yes, string 'null'). Why? Who knows. I blame PHP.
             */
            if (webDiscountGroups && webDiscountGroups !== 'null') {
                this.discountGroupOptions = [
                    // don't forget about a virtual "Adult" group
                    groups.find((g) => g.id === null),
                    ...webDiscountGroups
                        ?.split(',')
                        .map((groupId) => groups.find((g) => g.group_id === Number(groupId)))
                ].filter(Boolean);
            } else {
                this.discountGroupOptions = groups;
            }
            this.setAdultTranslation();
            this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
                this.locale = this.translate.currentLang;
                this.setAdultTranslation();
            });
        });
    }

    private setAdultTranslation() {
        const adultGroup = this.discountGroupOptions.find((g) => g.id === null);
        if (adultGroup) {
            const adultTranslation = adultGroup.translations.find((t) => t.lang === this.locale);
            if (adultTranslation) {
                adultTranslation.translation = this.translate.instant('ADULT');
            }
        }
    }

    private createFormGroup(editableCard?: RelatedCard) {
        const formGroup = new FormGroup({
            cardNr: new FormControl({ value: editableCard?.card_nr ?? '', disabled: this.mode === 'edit' }, [
                Validators.required,
                Validators.pattern('[0-9]*')
            ]),
            name: new FormControl(editableCard?.name ?? '', [Validators.required])
        });

        if (this.showDiscounts) {
            const initialValue = editableCard?.groups;
            const normalizedValue =
                initialValue !== null && initialValue !== undefined && initialValue !== '' ? Number(initialValue) : '';
            formGroup.addControl('discountGroup', new FormControl(normalizedValue));
        }

        if (this.mode === 'add' && !this.hidePinField) {
            formGroup.addControl(
                'cardPin',
                new FormControl('', [
                    Validators.required,
                    Validators.pattern('[0-9]*'),
                    Validators.minLength(4),
                    Validators.maxLength(4)
                ])
            );
        }

        this.cardForm = formGroup;
        setTimeout(() => this.formChange.emit(this.cardForm));
    }

    private fetchCardDiscountOptions() {
        if (this.showDiscounts) {
            this.relatedCardFacade.fetchCardsDiscountGroups();
        }
    }

    get showCardNumberError(): boolean {
        return this.cardForm?.get('cardNr')?.invalid && this.cardForm?.get('cardNr')?.dirty;
    }

    get showCardPinError(): boolean {
        return this.cardForm?.get('cardPin')?.invalid && this.cardForm?.get('cardPin')?.dirty;
    }

    get showCardNameError(): boolean {
        return this.cardForm?.get('name')?.invalid && this.cardForm?.get('cardPin')?.dirty;
    }

    get cardNumberValid(): boolean {
        return this.cardForm?.get('cardNr')?.valid;
    }

    get cardPinValid(): boolean {
        return this.cardForm?.get('cardPin')?.valid;
    }

    get cardNameValid(): boolean {
        return this.cardForm?.get('name')?.valid;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
